import React from 'react'
import Modal from '../Modal/Modal'
import { GearIcon } from '../../../assets/icons'
import styles from './ColumnSettings.module.scss'

export interface ColumnDefinition<K extends string> {
  key: K
  label: string
}

export interface ColumnSettingsProps<K extends string> {
  isOpen: boolean
  onClose: () => void
  columns: ReadonlyArray<ColumnDefinition<K>>
  visibleKeys: K[]
  onChange: (next: K[]) => void
}

export default function ColumnSettings<K extends string>({
  isOpen,
  onClose,
  columns,
  visibleKeys,
  onChange,
}: ColumnSettingsProps<K>) {
  const visibleSet = React.useMemo(() => new Set(visibleKeys), [visibleKeys])

  const toggle = (key: K) => {
    const next = new Set(visibleSet)
    if (next.has(key)) {
      if (next.size <= 1) return
      next.delete(key)
    } else {
      next.add(key)
    }
    onChange(columns.filter((c) => next.has(c.key)).map((c) => c.key))
  }

  const reset = () => {
    onChange(columns.map((c) => c.key))
  }

  return (
    <Modal isOpen={isOpen} handleClose={onClose}>
      <div className={styles.container}>
        <h3 className={styles.title}>Colonnes affichées</h3>
        <p className={styles.subtitle}>
          Sélectionnez les colonnes à afficher dans le tableau.
        </p>
        <div className={styles.list}>
          {columns.map((col) => {
            const checked = visibleSet.has(col.key)
            return (
              <label key={col.key} className={styles.item}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={checked}
                  onChange={() => toggle(col.key)}
                />
                <span className={styles.label}>{col.label}</span>
              </label>
            )
          })}
        </div>
        <div className={styles.actions}>
          <button type="button" className={styles.resetButton} onClick={reset}>
            Tout afficher
          </button>
          <button type="button" className={styles.confirmButton} onClick={onClose}>
            Fermer
          </button>
        </div>
      </div>
    </Modal>
  )
}

export interface ColumnSettingsToolbarProps {
  onClick: () => void
  ariaLabel?: string
  className?: string
}

export function ColumnSettingsToolbar({
  onClick,
  ariaLabel = 'Configurer les colonnes',
  className,
}: ColumnSettingsToolbarProps) {
  return (
    <div className={[styles.toolbar, className].filter(Boolean).join(' ')}>
      <button
        type="button"
        className={styles.gearButton}
        onClick={onClick}
        aria-label={ariaLabel}
        title={ariaLabel}
      >
        <GearIcon size={20} color="#4b5563" />
      </button>
    </div>
  )
}

// defaultKeys : colonnes visibles tant que l'utilisateur n'a rien choisi
// (une colonne ajoutée peut naître masquée sans déranger les habitudes).
export function loadColumnPreference<K extends string>(
  storageKey: string,
  allKeys: ReadonlyArray<K>,
  defaultKeys: ReadonlyArray<K> = allKeys,
): K[] {
  if (typeof window === 'undefined') return [...defaultKeys]
  try {
    const raw = window.localStorage.getItem(storageKey)
    if (!raw) return [...defaultKeys]
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return [...defaultKeys]
    const allowed = new Set<string>(allKeys)
    const filtered = parsed.filter((v): v is K => typeof v === 'string' && allowed.has(v))
    if (filtered.length === 0) return [...defaultKeys]
    return filtered
  } catch {
    return [...defaultKeys]
  }
}

export function saveColumnPreference<K extends string>(
  storageKey: string,
  visibleKeys: K[],
): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(visibleKeys))
  } catch {
    // ignore storage errors (quota, private mode)
  }
}
